import { PauseCircle } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"

interface PausedBannerProps {
  brandName: string
  /** Pre-formatted, locale-aware pause date, or `null` when unknown. */
  pauseDate: string | null
  /**
   * OKS-561 — `true` when the pause was a brand-admin action (source
   * `brand_admin`). Prefixes the body with "Admin action ·" so the member
   * understands the brand made the call (and reactivation is a request, not
   * a self-serve tap). `false`/omitted = member-self paused.
   */
  brandPaused?: boolean
}

/**
 * OKS-404 / OKS-561 — Muted pause banner (UX Spec §2.1).
 *
 * Intentionally the `default` (neutral/card) Alert variant, NOT
 * `destructive` — the tone is "see you soon," not an error. `role="status"`
 * (overriding the Alert default `role="alert"`) so screen readers announce
 * it politely. The full meaning is carried by the text, not the muted
 * color (WCAG — color is not the sole indicator). Static: no dismiss, since
 * the banner is the page's reason for being.
 *
 * When `pauseDate` is `null` (no pause event recorded) the body gracefully
 * drops the "on {date}" clause. When `brandPaused` is set, an "Admin
 * action ·" prefix precedes the body text (still text, not color).
 */
export function PausedBanner({
  brandName,
  pauseDate,
  brandPaused = false,
}: PausedBannerProps) {
  const { t } = useTranslation()

  const body = pauseDate
    ? t("crew_paused.banner.body", { brand: brandName, date: pauseDate })
    : t("crew_paused.banner.body_no_date", { brand: brandName })

  return (
    <Alert role="status" data-testid="paused-banner">
      <PauseCircle aria-hidden="true" />
      <AlertTitle>{t("crew_paused.banner.title")}</AlertTitle>
      <AlertDescription>
        <p>
          {brandPaused ? (
            <span
              className="text-foreground font-medium"
              data-testid="paused-banner-admin-prefix"
            >
              {t("crew_paused.banner.admin_prefix")}{" "}
            </span>
          ) : null}
          {body}
        </p>
      </AlertDescription>
    </Alert>
  )
}
